import { useMemo, useState } from 'react';
import { AlertTriangle, Mail, MapPin, Pencil, Phone, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResourceBadge } from '@/components/ui/resource-badge';
import { OrderDetail } from '@/lib/api/orders';
import { CustomerResourceBadge } from '@/components/customer/CustomerResourceBadge';
import { EditAddressDialog } from './edit-address-dialog';

const strictAddressSchema = z.object({
  company: z.string().optional(),
  address1: z.string().min(1),
  address2: z.string().optional(),
  lastName: z.string().trim().min(1),
  firstName: z.string().trim().min(1),
  city: z.string().trim().min(1),
  state: z.string().min(1),
  country: z.string().min(1),
  postal: z.string().trim().min(1),
  phone: z.string().optional(),
  email: z.string().optional(),
});

function validateAddress(address: unknown): z.ZodError | null {
  const result = strictAddressSchema.safeParse(address);
  return result.success ? null : result.error;
}

interface OrderCustomerCardProps {
  order: OrderDetail;
}

function AddressSection({ title, address }: { title: string; address: any }) {
  const validationError = useMemo(() => validateAddress(address), [address]);

  if (!address) {
    return (
      <div className="text-muted-foreground text-sm">
        <p>No {title.toLowerCase()} provided</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 text-sm">
      {validationError && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Incomplete address</AlertTitle>
          <AlertDescription>
            Missing: {validationError.issues.map((i) => i.path.join('.')).join(', ')}
          </AlertDescription>
        </Alert>
      )}
      {(address.firstName || address.lastName) && (
        <p className="font-medium">
          {address.firstName} {address.lastName}
        </p>
      )}
      {address.company && <p>{address.company}</p>}
      {address.address1 && <p>{address.address1}</p>}
      {address.address2 && <p className="text-muted-foreground">{address.address2}</p>}
      <p>{[address.city, address.state, address.postal].filter(Boolean).join(', ')}</p>
      {address.country && <p>{address.country}</p>}
      {address.phone && (
        <div className="flex items-center gap-2 pt-2">
          <Phone className="h-3.5 w-3.5" />
          <span>{address.phone}</span>
        </div>
      )}
    </div>
  );
}

export function OrderCustomerCard({ order }: OrderCustomerCardProps) {
  const { t } = useTranslation('orders');
  const [isEditAddressOpen, setIsEditAddressOpen] = useState(false);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('customer')}</CardTitle>
            <CustomerResourceBadge
              customerId={order.customer.id}
              variant="outline"
            />
          </div>
        </CardHeader>
        <CardContent>
          {/* Customer Info */}
          <div className="mb-6 space-y-3">
            <div>
              <span className="text-muted-foreground text-sm">{t('name')}</span>
              <p className="font-medium">
                {order.customer.firstName} {order.customer.lastName}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground text-sm">{t('email')}</span>
              <div className="flex items-center gap-2">
                <Mail className="text-muted-foreground h-4 w-4" />
                <p className="font-medium">{order.customer.email}</p>
              </div>
            </div>
            {(order.customer as any).phone && (
              <div>
                <span className="text-muted-foreground text-sm">{t('phone')}</span>
                <div className="flex items-center gap-2">
                  <Phone className="text-muted-foreground h-4 w-4" />
                  <p className="font-medium">{(order.customer as any).phone}</p>
                </div>
              </div>
            )}
          </div>

          <Separator className="my-6" />

          {/* Addresses */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{t('addresses')}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setIsEditAddressOpen(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Tabs defaultValue="shipping" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="shipping" className="gap-2">
                <MapPin className="h-4 w-4" />
                {t('shipping')}
              </TabsTrigger>
              <TabsTrigger value="billing" className="gap-2">
                <MapPin className="h-4 w-4" />
                {t('billing')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="shipping" className="mt-4">
              <AddressSection title={t('shippingAddress')} address={order.shippingAddress} />
            </TabsContent>
            <TabsContent value="billing" className="mt-4">
              <AddressSection title={t('billingAddress')} address={order.billingAddress} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <EditAddressDialog
        open={isEditAddressOpen}
        onOpenChange={setIsEditAddressOpen}
        orderId={order.id}
        shippingAddress={order.shippingAddress}
        billingAddress={order.billingAddress}
      />
    </>
  );
}
